<template>
  <v-container
    id="app-home"
    :class="{ 'fill-height home-shortcut': shortcut && !modeSessionFloating}"
  >
    <div
      class="bloc"
      :class="{ 'pa-1': shortcut && !modeSessionFloating}"
    >
      <!-- ******************************
      ********** SHORTCUT MODE **********
      ******************************* -->
      <section v-if="modeSessionFloating">
        <contactless
          :action="cashier"
          :use-listener="true"
          :reset-scan="resetScan"
          :total="10"
          :ismode-session-floating="modeSessionFloating"
          @scan="launchScan"
        />
      </section>
      <shortcut
        v-if="shortcut && !modeSessionFloating"
        :is-gift-only="isGiftOnly"
        :is-mobil="isMobil"
      />
      <!-- *****************************
      ********** CLASSIC HOME **********
      ****************************** -->
      <section v-if="!shortcut && !modeSessionFloating">
        <amount
          :text="text"
          :info="info"
          @show="shoppingDialog = true"
        />

        <action-choice
          v-if="(IS_PAX || IS_INGENICO) && !productKeyboard"
          :show="showActionChoice"
          @title="(t) => { $emit('title', t) }"
        />

        <v-dialog
          v-model="menuDialog"
          persistent
          fullscreen
        >
          <action-menu
            @action="menu"
            @hide="menuDialog = false"
          />
        </v-dialog>

        <keyboard
          v-if="!productKeyboard"
          v-model="total"
          from-home
          :contact-disabled="offline"
          :scan-disabled="(!useInvoicePayment || total > 0) && !vchAuthorization"
          :w2s-disabled="!useW2S"
          @menu="menu"
          @menu-longpress="menuDialog = true"
          @func="func"
          @cancel="cancel"
          @valid="valid"
          @carrier="toCarrier"
          @w2s="toW2S"
          @badge="toBadgeInfos"
          @scan="toScan"
        />
      </section>
    </div>
  </v-container>
</template>

<script>
import Amount from '../components/Amount'
import ActionChoice from '../components/ActionChoice'
import ActionMenu from '../components/modal/ActionMenu'

import Shortcut from '../components/home/Shortcut'

import {ActionEnum, LimitTransactionHard, LimitTransactionLight} from '@/modules/Enums'
import { Ticket } from '@/modules/Ticket'
import { $alert, $amount } from '@/modules/Utils'
import Contactless from '@/components/scan/Contactless.vue';
import {MetierLocalStorage} from 'modulelocalstorage';

export default {
  name: 'HomeView',

  components: {
    Contactless,
    Amount,
    ActionChoice,
    ActionMenu,
    Shortcut
  },

  props: {
    isSearchField : { type: Boolean, default: true },
    reset: { type: Boolean, default: true },
    reference: { type: String, default: null },
    isCbCashless: { type: Boolean, default: false }
  },

  data () {
    return {
      IS_PAX,
      IS_INGENICO,
      //
      refExterne: this.reference,
      shoppingDialog: false,
      menuDialog: false,
      paymentType: 0,
      resetScan: false,
    }
  },

  computed: {
    total: {
      get () { return this.$store.state.amount },
      set (val) { this.$store.commit('amount', val) }
    },
    text () {
      if (!this.offline) {
        return null
      }
      return this.$i18n.t('info.trx_to_send') + ' : ' +
        this.$store.state.transactions.cashless + '/' + LimitTransactionHard + ' - ' +
        $amount(this.$store.state.transactions.cashlessCa)
    },
    info () {
      return this.paymentType === 1 ? 'Credit' : (this.paymentType === 2 ? this.$i18n.t('dialog.cancellation_title') : null)
    },
    nbTransactions () { return this.$store.getters.totalTransactions },
    menuItems () { return this.$store.state.menuItems },
    showActionChoice () { return (IS_PAX || IS_INGENICO) && this.total > 0 },
    productKeyboard () { return this.$store.getters.productslength > 0 },
    offline () { return this.$store.state.offline },
    neptingInfos () { return this.$store.state.neptingInfos },
    isMobil () { return this.$store.getters.isMobil },
    isGiftOnly () { return this.$store.getters.isGiftOnly },
    useInvoicePayment () { return this.$store.getters.useInvoicePayment },
    shortcut () { return this.isGiftOnly || this.isMobil },
    useBanking () { return this.$store.getters.useBanking },
    useW2S () { return this.$store.getters.useW2S },
    vchAuthorization () { return this.$store.getters.vchAuthorization },
    fromExternalAction () { return this.$store.getters.externalAction !== null },
    modeSessionFloating() {
      let teleparameters = this.$store.state.teleparameters;
      let connectedCashier = MetierLocalStorage.getConnectedCashier();

      let mode = teleparameters?.CashierEngagementMode ?? null;
      let cashiers = teleparameters?.Cashiers ?? [];

      return (mode === 'SESSION' || mode === 'FLOATING') && cashiers.length > 0 && !connectedCashier;
    }
  },

  mounted () {
    if (this.fromExternalAction) {
      // Si on vient d'une action extérieure, on retourne une erreur
      CissAndroid.sendError({ message: 'Annulation par l\'utilisateur' })
      return
    }

    this.$store.commit('drawer', false)
    this.$emit('title', this.$store.state.merchantName)

    if (this.$store.state.teleparameters === null) {
      this.$router.replace('/')
    } else {
      if (this.reset) this.resetTotal()
      if (this.productKeyboard) this.$emit('show-products')
    }

    // Reset des éventuels anciens tickets bancaires
    Ticket.bankingTickets = []
  },

  beforeDestroy () {
    this.$emit('hide-products')
  },

  methods: {
    resetTotal () {
      this.$store.commit('donationAmount', 0)
      this.$store.commit('tipAmount', 0)
      this.total = 0
      this.paymentType = 0
      if (this.productKeyboard) this.$store.dispatch('resetProducts')
    },
    // ********** KEYBOARD **********
    cancel () {
      this.resetTotal()
    },
    valid () {
      if (this.total > 0) {
        if (this.offline) {
          // Controle limite hard transactions
          if (this.nbTransactions >= LimitTransactionHard) {
            $alert(this.$i18n.t('error.offline_trx_max'))
            return
          }
          // Controle limite light transactions
          if (this.nbTransactions >= LimitTransactionLight) {
            $alert(this.$i18n.t('info.offline_trx_light'), 'info')
          }
        }

        if (this.useBanking) {
          // Redirection vers le paiement bancaire
          this.$router.replace({ name: 'banking-payment', params: { paymentType: this.paymentType } })
        } else {
          // Redirection vers la page de choix d'action
          this.$router.replace('/action-choice')
        }
      }
    },
    menu (i = null, t = null) {
      if (this.offline) {
        // Reset total
        this.total = 0
        // Set title
        this.$emit('title', this.$i18n.t('button.shortcut_badge'))
        // Redirect to nfc offline scan
        this.$router.replace({
          name: 'scan-nfc-offline',
          params: { action: ActionEnum.badgeInformations, backToHome: true }
        })
      } else {
        // prevent touchend ios
        if ((i === null && !this.menuDialog) || (i !== null && this.menuDialog)) {
          // Index option favori ou index sélectionné
          let index = i !== null ? i : this.$store.state.favoriteItem
          if (index !== null) {
            // Redirection vers option[index]
            let title = t !== null ? t : this.$i18n.t(this.menuItems[index].text)
            this.total = 0
            this.$emit('title', title)
            this.menuDialog = false
            this.$nextTick(() => {
              this.$router.replace({
                name: this.menuItems[index].route,
                params: { action: this.menuItems[index].action, mode: 'P', backToHome: true }
              })
            })
          } else {
            // Ouverture modale choix option
            this.menuDialog = true
          }
        }
      }
    },
    func () {
      if (this.$store.getters.useBanking) {
        if (this.paymentType === 2) {
          this.paymentType = 0
        } else {
          this.paymentType++
        }
      }
    },
    toW2S () {
      this.$router.replace('/w2s')
    },
    toCarrier () {
      this.$router.replace('customer-search')
    },
    toBadgeInfos () {
      // Reset total
      this.total = 0
      // Redirection vers badge infos
      this.$router.replace({
        name: this.offline ? 'scan-nfc-offline' : 'action-choice',
        params: { action: ActionEnum.badgeInformations, mode: 'P', backToHome: true }
      })
    },
    toScan () {
      let action = null
      if (this.vchAuthorization) {
        action = ActionEnum.voucher
        this.$emit('title', this.$t('title.vouchers'))
      } else {
        action = ActionEnum.invoicePayement
        this.$emit('title', this.$t('button.shortcut_invoice_payment'))
      }

      this.$router.replace({
        name: 'scan-qrcode',
        params: {
          action: action,
          backToHome: true
        }
      })
    },
    launchScan (uid) {
        // Trouver le caissier dont le nfc_card_number correspond à l'uid
        const connectedCashier = this.$store.state.teleparameters.Cashiers.find(cashier =>
          cashier.cards.some(card => card.nfc_card_number === uid)
        );
        if (connectedCashier) {
          MetierLocalStorage.setConnectedCashier(connectedCashier)
          this.$store.state.connectedCashier = connectedCashier
          this.$router.replace('/')
        } else {
          $alert('Aucun caissier trouvé');
          // Restart scan
          this.resetScan = !this.resetScan
          return
        }
    }
  }
}
</script>
